import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { configAPI } from '../services/api';
import PlatformConfigEditModal from '../components/config/PlatformConfigEditModal';
import SettingsEditModal from '../components/config/SettingsEditModal';
import ResourceSelector from '../components/config/ResourceSelector';

const ConfigurationPage = () => {
  const [activeSetup, setActiveSetup] = useState('local');
  const [configPayload, setConfigPayload] = useState(null);
  const [models, setModels] = useState([]);
  const [embeddings, setEmbeddings] = useState([]);
  const [vectorDbs, setVectorDbs] = useState([]);
  const [visionModels, setVisionModels] = useState([]);
  const [visionModelsLoaded, setVisionModelsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [selectorType, setSelectorType] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSetupSwitch, setPendingSetupSwitch] = useState(null);

  const flattenOptions = (options = {}) =>
    Object.entries(options).flatMap(([provider, items]) =>
      (items || []).map((item) => ({ ...item, provider })),
    );

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const response = await configAPI.getActiveSetup();
      console.log('📖 Loaded config response:', response);
      
      if (response.success && response.data) {
        const data = response.data;
        // Use setupType from response or detect from environment field
        const activeKey = response.setupType || data.environment || 'local';
        
        setConfigPayload(data);
        setActiveSetup(activeKey);

        // Use available_options for models, embeddings, vector_dbs
        const availableOptions = data.available_options || {};
        setModels(flattenOptions(availableOptions.model_options));
        setEmbeddings(flattenOptions(availableOptions.embedding_models));
        setVectorDbs(flattenOptions(availableOptions.vector_dbs));
        setVisionModels(availableOptions.vision_models || {});
        console.log('✅ Configuration loaded successfully, environment:', activeKey);
      } else {
        toast.error('Invalid configuration data received');
      }
    } catch (error) {
      console.error('❌ Failed to load configuration', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load configuration: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadVisionModels = async () => {
    if (visionModelsLoaded) return;
    
    try {
      const response = await configAPI.getVisionModels();
      console.log('📷 Vision models response:', response);
      if (response.success) {
        setVisionModels(response.models || {});
      }
      setVisionModelsLoaded(true);
    } catch (error) {
      console.error('Error loading vision models:', error);
      setVisionModelsLoaded(true);
    }
  };

  useEffect(() => {
    loadConfiguration();
    loadVisionModels();
  }, []);

  const confirmPlatformChange = (title, details) => {
    return window.confirm(
      `${title}\n\n${details}\n\nOnly continue if this is an intentional superadmin change.`
    );
  };

  // Show confirmation dialog before switching
  const handleSetupSwitchRequest = (setupKey) => {
    if (setupKey === activeSetup) return; // Already on this setup
    
    setPendingSetupSwitch(setupKey);
    setShowConfirmDialog(true);
  };

  // Confirm and execute the switch
  const confirmSwitchSetup = async () => {
    if (!pendingSetupSwitch) return;
    
    setShowConfirmDialog(false);
    setPendingSetupSwitch(null);
    
    // Environment is auto-detected based on deployment, switching is not supported
    toast.error('Environment switching is not supported. Environment is auto-detected based on deployment.');
  };

  // Cancel the switch
  const cancelSwitchSetup = () => {
    setShowConfirmDialog(false);
    setPendingSetupSwitch(null);
  };

  const handleSaveConfig = async (formData) => {
    if (!confirmPlatformChange(
      'Save active platform configuration?',
      'This can change the live LLM, embedding, vector database, parser, or endpoint settings used by agents.'
    )) {
      return;
    }

    try {
      console.log('💾 Saving configuration:', formData);
      
      const response = await configAPI.updateActiveConfig(formData);
      
      console.log('✅ Save response:', response);
      
      if (response.success) {
        toast.success(response.message || 'Configuration updated successfully');
        setShowConfigModal(false);
        
        // Reload configuration
        await loadConfiguration();
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('❌ Error saving configuration:', error);
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to update configuration';
      
      toast.error(errorMessage);
    }
  };

  const handleSaveSettings = async (formData) => {
    if (!confirmPlatformChange(
      'Save platform settings?',
      'This can change global limits and defaults used across the platform.'
    )) {
      return;
    }

    try {
      console.log('💾 Saving settings:', formData);
      
      const response = await configAPI.updateSettings(formData);
      
      console.log('✅ Settings save response:', response);
      
      if (response.success) {
        toast.success(response.message || 'Settings updated successfully');
        setShowSettingsModal(false);
        
        // Reload configuration
        await loadConfiguration();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('❌ Error saving settings:', error);
      
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to save settings';
      
      toast.error(errorMessage);
    }
  };

  const openResourceSelector = (type) => {
    setSelectorType(type);
    // Use unified active_config from new config structure
    const activeConfig = configPayload?.active_config || {};
    
    if (type === 'model') {
      setSelectedResource({ 
        name: activeConfig.llm_model_name, 
        provider: activeConfig.llm_provider 
      });
    }
    if (type === 'embedding') {
      setSelectedResource({
        name: activeConfig.embedding_model_name,
        provider: activeConfig.embedding_provider,
      });
    }
    if (type === 'vector-db') {
      setSelectedResource({
        name: activeConfig.vector_db_name,
        provider: activeConfig.vector_db_provider,
      });
    }
    setShowResourceSelector(true);
  };

  const handleSelectResource = async (resource) => {
    if (!resource) return;

    if (!confirmPlatformChange(
      `Switch ${selectorType} resource?`,
      `Selected resource: ${resource.provider || 'unknown'} / ${resource.name || 'unknown'}`
    )) {
      return;
    }
    
    const updates = {};
    
    if (selectorType === 'model') {
      updates.llm_provider = resource.provider;
      updates.llm_model_name = resource.name;
      if (resource.endpoint) updates.llm_model_endpoint = resource.endpoint;
      if (resource.apiversion) updates.llm_model_apiversion = resource.apiversion;
      if (resource.temperature !== undefined) updates.llm_model_temperature = resource.temperature;
    }
    if (selectorType === 'embedding') {
      updates.embedding_provider = resource.provider;
      updates.embedding_model_name = resource.name;
      if (resource.endpoint) updates.embedding_model_endpoint = resource.endpoint;
      if (resource.apiversion) updates.embedding_model_apiversion = resource.apiversion;
    }
    if (selectorType === 'vector-db') {
      updates.vector_db_provider = resource.provider;
      updates.vector_db_name = resource.name;
      if (resource.collection_name) updates.vector_db_collection_name = resource.collection_name;
      if (resource.endpoint) updates.vector_db_endpoint = resource.endpoint;
    }
    
    try {
      // Save to backend
      await configAPI.updateActiveConfig(updates);
      toast.success('Resource updated successfully');
      
      // Reload configuration
      await loadConfiguration();
      setShowResourceSelector(false);
    } catch (error) {
      console.error('❌ Error updating resource:', error);
      toast.error('Failed to update resource');
    }
  };

  const groupedModels = useMemo(() => {
    const grouped = {};
    models.forEach((model) => {
      grouped[model.provider] = grouped[model.provider] || [];
      grouped[model.provider].push(model);
    });
    return grouped;
  }, [models]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading platform configuration...</div>
      </div>
    );
  }

  if (!configPayload) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium mb-2">Unable to load platform configuration</h3>
          <p className="text-red-600 text-sm mb-3">
            The backend API is not responding. Please ensure:
          </p>
          <ul className="list-disc list-inside text-red-600 text-sm space-y-1 mb-3">
            <li>Backend server is running at {process.env.REACT_APP_API_URL || 'http://localhost:8000'}</li>
            <li>Configuration file exists at: serviti-config/platform-config/serviti_ai_config.json</li>
            <li>No CORS errors in browser console</li>
          </ul>
          <button 
            onClick={loadConfiguration}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Get active config based on setup type
  const setupType = configPayload.setupType || configPayload.environment || 'local';
  const activeConfig = configPayload.active_config || {};
  const settings = configPayload.settings || {};

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Serviti AI Platform Configuration</h1>
          <p className="mt-2 text-gray-600">Manage your Serviti AI platform settings and configurations</p>
        </div>
      
        <div className="space-y-6">
          {/* Active Setup Type - WITH TOGGLE */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Active Setup</h2>
                <p className="mt-1 text-sm text-gray-500">Current platform configuration mode</p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Setup Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => handleSetupSwitchRequest('local')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      setupType === 'local'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Local
                    </span>
                  </button>
                  <button
                    onClick={() => handleSetupSwitchRequest('cloud')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      setupType === 'cloud'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                      </svg>
                      Cloud
                    </span>
                  </button>
                </div>
                
                {/* Status Badge - Shows active_config enabled state */}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  activeConfig?.enabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {activeConfig?.enabled ? '● Enabled' : '○ Disabled'}
                </span>
              </div>
            </div>
            
            {/* Setup Info */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Vector DB:</span>
                  <span className="ml-2 font-medium">
                    {activeConfig?.vector_db_provider || 'Not Configured'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">LLM Providers:</span>
                  <span className="ml-2 font-medium">
                    {(configPayload[setupType]?.llms || []).join(', ') || 'None'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Available Models:</span>
                  <span className="ml-2 font-medium">
                    {models.length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Embeddings:</span>
                  <span className="ml-2 font-medium">
                    {embeddings.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Current Configuration - Main Display */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Current Configuration 
                  <span className={`ml-2 text-sm font-normal px-2 py-1 rounded ${
                    setupType === 'cloudsetup' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {setupType === 'cloudsetup' ? 'Cloud' : 'Local'}
                  </span>
                </h2>
                <p className="mt-1 text-sm text-gray-500">Active AI models and database settings for {setupType === 'cloudsetup' ? 'cloud' : 'local'} environment</p>
              </div>
              <button
                onClick={() => setShowConfigModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Edit Active Configuration
              </button>
            </div>

            <div className="space-y-6">
              {/* LLM Configuration */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  LLM Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Provider</span>
                    <span className="font-medium text-gray-900">{activeConfig.llm_provider || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Model</span>
                    <span className="font-medium text-gray-900">{activeConfig.llm_model_name || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Temperature</span>
                    <span className="font-medium text-gray-900">{activeConfig.llm_model_temperature || 'Not Set'}</span>
                  </div>
                  {activeConfig.llm_model_endpoint && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">Endpoint</span>
                      <span className="font-medium text-gray-900 text-xs truncate block">{activeConfig.llm_model_endpoint}</span>
                    </div>
                  )}
                  {activeConfig.llm_model_apiversion && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">API Version</span>
                      <span className="font-medium text-gray-900">{activeConfig.llm_model_apiversion}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Embedding Configuration */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  Embedding Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Provider</span>
                    <span className="font-medium text-gray-900">{activeConfig.embedding_provider || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Model</span>
                    <span className="font-medium text-gray-900">{activeConfig.embedding_model_name || 'Not Set'}</span>
                  </div>
                  {activeConfig.embedding_model_endpoint && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">Endpoint</span>
                      <span className="font-medium text-gray-900 text-xs truncate block">{activeConfig.embedding_model_endpoint}</span>
                    </div>
                  )}
                  {activeConfig.embedding_model_apiversion && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">API Version</span>
                      <span className="font-medium text-gray-900">{activeConfig.embedding_model_apiversion}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Vector Database Configuration */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                  </svg>
                  Vector Database
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Provider</span>
                    <span className="font-medium text-gray-900">{activeConfig.vector_db_provider || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Database Name</span>
                    <span className="font-medium text-gray-900">{activeConfig.vector_db_name || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Collection</span>
                    <span className="font-medium text-gray-900">{activeConfig.vector_db_collection_name || 'Not Set'}</span>
                  </div>
                  {activeConfig.vector_db_endpoint && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">Endpoint</span>
                      <span className="font-medium text-gray-900 text-xs truncate block">{activeConfig.vector_db_endpoint || 'Local'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* PDF Parser */}
              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Document Processing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">PDF Parser</span>
                    <span className="font-medium text-gray-900">{activeConfig.pdf_parser || 'Not Set'}</span>
                  </div>
                </div>
              </div>

              {/* Vision Model Configuration */}
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Vision Model Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Provider</span>
                    <span className="font-medium text-gray-900">{activeConfig.vision_llm_provider || 'Not Set'}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Model</span>
                    <span className="font-medium text-gray-900">{activeConfig.vision_llm_model_name || 'Not Set'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Platform Settings</h2>
                <p className="mt-1 text-sm text-gray-500">General platform configuration settings</p>
              </div>
              <button
                onClick={() => setShowSettingsModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Edit Settings
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1">Max Documents</span>
                <span className="text-2xl font-bold text-gray-900">{settings.max_documents || 'N/A'}</span>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1">Max File Size</span>
                <span className="text-2xl font-bold text-gray-900">{settings.max_file_size_mb || 'N/A'} <span className="text-sm text-gray-500">MB</span></span>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <span className="text-xs text-gray-500 block mb-1">Default Temperature</span>
                <span className="text-2xl font-bold text-gray-900">{settings.default_temperature || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Raw JSON View (collapsible) */}
          <details className="bg-white rounded-lg shadow-sm border border-gray-200">
            <summary className="cursor-pointer p-6 font-semibold text-gray-900 hover:bg-gray-50">
              View Raw Configuration (JSON)
            </summary>
            <div className="px-6 pb-6">
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-96">
                <pre className="text-sm">
                  {JSON.stringify(configPayload, null, 2)}
                </pre>
              </div>
            </div>
          </details>
        </div>
      </div>

      {/* Modals */}
      <PlatformConfigEditModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        config={configPayload}
        onSave={handleSaveConfig}
        editMode="active_config"
        visionModels={visionModels}  // Add this line
      />

      <SettingsEditModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={settings}
        onSave={handleSaveSettings}
      />

      {showResourceSelector && (
        <ResourceSelector
          resources={
            selectorType === 'model'
              ? models
              : selectorType === 'embedding'
              ? embeddings
              : selectorType === 'vector-db'
              ? vectorDbs
              : []
          }
          selectedResource={selectedResource}
          onSelect={handleSelectResource}
          type={selectorType}
          isOpen={showResourceSelector}
          onClose={() => setShowResourceSelector(false)}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4 sm:mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Setup Switch Blocked</h3>
            <p className="text-sm text-gray-700 mb-4">
              You clicked <span className="font-medium">{pendingSetupSwitch === 'cloud' ? 'Cloud' : 'Local'}</span> setup.
              Runtime environment switching is intentionally blocked here so cloud/local changes cannot happen by accident.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelSwitchSetup}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmSwitchSetup}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationPage;
